import React from 'react';

import { CSSObject } from 'styled-components';

import Avatar from '@adsk/alloy-react-avatar';
import {
  GetSelectedItemProps,
  ItemToString,
  Token,
} from '@adsk/alloy-react-dropdown';
import {
  BuildingIcon,
  PeopleIcon,
  RenderIconType,
} from '@adsk/alloy-react-icon';
import Theme from '@adsk/alloy-react-theme';

import { ASSIGNEE_PICKER_COMPANY, ASSIGNEE_PICKER_ROLE } from '../consts';
import { ItemWithGroup } from '../types';
import { getAssigneeItemKey } from './utils';

/**
 * Stable style variants for the token avatar, hoisted to module scope so the
 * object identities stay stable across renders. Members share one variant;
 * companies and roles share the tinted variant.
 */
const memberAvatarStyle = { marginRight: Theme.spacing.XXXS };
const nonMemberAvatarStyle = {
  marginRight: Theme.spacing.XXXS,
  backgroundColor: Theme.colors.charcoal100,
};

/**
 * Resolves the per-group fallback icon renderer for an item that has no avatar
 * image. Company entries fall back to a building icon, role entries fall back
 * to a people icon, and user entries fall back to `Avatar`'s own initials
 * treatment (no icon, hence `null`).
 *
 * Shared by `AssigneePickerMenuItem` (dropdown row) and the selected-token
 * avatar so the two render sites cannot silently diverge.
 */
export const getFallbackIconRenderer = (
  group: ItemWithGroup['group'],
): RenderIconType | null => {
  if (group === ASSIGNEE_PICKER_COMPANY) {
    return BuildingIcon.renderIcon;
  }

  if (group === ASSIGNEE_PICKER_ROLE) {
    return PeopleIcon.renderIcon;
  }

  return null;
};

/** Whether an item represents a person (as opposed to a company or role). */
export const isMemberItem = (group: ItemWithGroup['group']) =>
  group !== ASSIGNEE_PICKER_COMPANY && group !== ASSIGNEE_PICKER_ROLE;

/**
 * Renders the same `Avatar` (image, or per-group fallback) `AssigneePickerMenuItem`
 * draws for a dropdown row, sized and styled for a selected token's smaller
 * left-component slot. Kept separate from the dropdown's row-sized instance
 * (`Avatar.SIZES.MEDIUM_32`) so token layout can use its own size without the
 * two call sites needing to agree on anything beyond image/fallback selection.
 *
 * `aria-hidden` because the token already shows the member's name as visible
 * text: `Avatar` derives its `<img alt>` from `name` (and cannot be given an
 * empty one without losing the initials fallback), so without this the name
 * would be announced twice and the token's accessible name would change.
 */
export const renderTokenAvatar = (item: ItemWithGroup) => {
  const isMember = isMemberItem(item.group);
  const renderFallbackIcon = getFallbackIconRenderer(item.group);

  return (
    <Avatar
      aria-hidden
      image={item.imageUrl}
      size={Avatar.SIZES.SMALL_16}
      name={item.label as string}
      style={isMember ? memberAvatarStyle : nonMemberAvatarStyle}
    >
      {renderFallbackIcon?.({ size: Theme.spacing.XS })}
    </Avatar>
  );
};

/**
 * `renderItems` for `@adsk/alloy-react-dropdown`'s `Multi*` controls that renders
 * each selected `AssigneePicker` item as a `Token` with that item's own avatar in
 * its `renderLeftComponent` slot, mirroring the dropdown row's avatar treatment.
 *
 * `useAssigneePicker` already returns this on `dropdownProps`, so multi-select
 * consumers get token avatars without wiring anything up — including those that
 * pass their own `renderControl`, since the multi `Dropdown` templates forward
 * `renderItems` into it. Override it with `useAssigneePicker({ renderItems })`.
 * Exported for apps that compose a token layout outside that flow.
 *
 * Each token resolves its avatar from its own `selectedItem`, so multiple
 * selected tokens never share or duplicate one another's avatar.
 */
export const renderAssigneePickerTokens = ({
  selectedItems,
  getSelectedItemProps,
  removeSelectedItem,
  itemToString,
  disabled: controlDisabled,
}: {
  selectedItems: ItemWithGroup[] | null;
  getSelectedItemProps: GetSelectedItemProps<ItemWithGroup>;
  removeSelectedItem: (item: ItemWithGroup) => void;
  itemToString: ItemToString<ItemWithGroup>;
  disabled?: boolean;
}) =>
  selectedItems?.map((selectedItem, index) => {
    const {
      style: tokenStyle,
      disabled: itemDisabled,
      ...selectedItemProps
    } = getSelectedItemProps({ selectedItem, index });

    return (
      <Token
        key={getAssigneeItemKey(selectedItem)}
        style={tokenStyle as CSSObject}
        onClickX={() => removeSelectedItem(selectedItem)}
        onRemove={() => removeSelectedItem(selectedItem)}
        renderLeftComponent={() => renderTokenAvatar(selectedItem)}
        {...selectedItemProps}
        content={itemToString?.(selectedItem)}
        disabled={itemDisabled || controlDisabled}
      />
    );
  });
